import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave,
  FiSearch, FiRefreshCw, FiBriefcase,
  FiCreditCard, FiUpload, FiFile, FiTrash,
  FiAlertCircle, FiFileText, FiUser, FiChevronRight, FiChevronLeft, FiCheck,
} from 'react-icons/fi';

/* ─────────────────────────────────────────────────────────
   BRAND PALETTE
───────────────────────────────────────────────────────── */
const B = {
  gold:        '#F5A623',
  goldLight:   '#FEF3DC',
  goldMid:     '#FBBF4A',
  navy:        '#0D3B5E',
  navyLight:   '#E8EFF5',
  navyMid:     '#1A5276',
  white:       '#FFFFFF',
  bg:          '#EEF1F5',
  border:      '#E4E8EE',
  text1:       '#0D1B2A',
  text2:       '#4A5568',
  text3:       '#9AA5B4',
  green:       '#16A34A',
  greenLight:  '#DCFCE7',
  greenBorder: '#86EFAC',
  red:         '#DC2626',
  redLight:    '#FEE2E2',
  redBorder:   '#FECACA',
  purple:      '#7C3AED',
  purpleLight: '#EDE9FE',
  sky:         '#0284C7',
  skyLight:    '#E0F2FE',
  amber:       '#D97706',
  amberLight:  '#FEF3C7',
};

const PAGE_SIZE    = 10;
const DRAWER_WIDTH = 520;

/* ─── Lookup tables ──────────────────────────────────────── */
const BSA_TYPES = [
  { value: 'BROKER',               label: 'Broker'               },
  { value: 'CA',                   label: 'CA / CS'              },
  { value: 'ARCHITECT',            label: 'Architect'            },
  { value: 'FINANCIAL_CONSULTANT', label: 'Financial Consultant' },
  { value: 'CORPORATE_FUNCTIONARY',label: 'Corporate Functionary'},
  { value: 'BUILDER',              label: 'Builder'              },
  { value: 'OTHER',                label: 'Other'                },
];

const CONSTITUTIONS = [
  { value: 'INDIVIDUAL',     label: 'Individual'    },
  { value: 'PARTNERSHIP',    label: 'Partnership'   },
  { value: 'PROPRIETORSHIP', label: 'Proprietorship'},
  { value: 'COMPANY',        label: 'Company'       },
];

const DOC_TYPES = [
  { value: 'KYC_ID',    label: 'KYC / ID Proof'  },
  { value: 'PAN',       label: 'PAN Card'         },
  { value: 'GST',       label: 'GST Certificate'  },
  { value: 'BANK',      label: 'Bank Statement'   },
  { value: 'AGREEMENT', label: 'Agreement'        },
];

const INCENTIVE_BASIS = [
  { value: 'DISBURSED_AMOUNT',  label: '% of Disbursed Amount'   },
  { value: 'NET_TO_COMPANY',    label: '% of Net to Company'      },
  { value: 'SANCTIONED_AMOUNT', label: '% of Sanctioned Amount'  },
  { value: 'FLAT',              label: 'Flat Fixed Amount'        },
];

const incentiveBasisLabel = (v) => INCENTIVE_BASIS.find(b => b.value === v)?.label || v || '—';

const TYPE_COLORS = {
  BROKER:               { bg: B.skyLight,   color: B.sky,    border: '#BAE6FD' },
  CA:                   { bg: B.purpleLight,color: B.purple, border: '#C4B5FD' },
  ARCHITECT:            { bg: '#FFF7ED',    color: '#C2410C',border: '#FED7AA' },
  FINANCIAL_CONSULTANT: { bg: B.navyLight,  color: B.navy,   border: '#BFDBFE' },
  CORPORATE_FUNCTIONARY:{ bg: B.goldLight,  color: '#92400E',border: '#FDE68A' },
  BUILDER:              { bg: B.greenLight, color: B.green,  border: B.greenBorder },
  OTHER:                { bg: '#F1F5F9',    color: B.text2,  border: B.border  },
};

const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#EF4444'];

/* ─── Steps definition ───────────────────────────────────── */
const STEPS = [
  { id: 1, label: 'Basic Info',    icon: <FiUser     size={13} /> },
  { id: 2, label: 'Bank Details',  icon: <FiCreditCard size={13} /> },
  { id: 3, label: 'Documents',     icon: <FiFileText size={13} /> },
];

/* ─── Helpers ─────────────────────────────────────────────── */
const avatarColor  = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const docTypeLabel = (v)    => DOC_TYPES.find(d => d.value === v)?.label     || v;
const bsaTypeLabel = (v)    => BSA_TYPES.find(d => d.value === v)?.label     || v;
const constLabel   = (v)    => CONSTITUTIONS.find(d => d.value === v)?.label || v;
const fileName     = (url)  => {
  if (!url) return 'Document';
  try { return decodeURIComponent(url.split('/').pop().split('?')[0]) || 'Document'; }
  catch { return 'Document'; }
};

/* ─── Empty form ──────────────────────────────────────────── */
const emptyForm = () => ({
  name: '', mobile_number: '', email: '',
  pan: '', gst_number: '', constitution: '',
  address: '', bsa_type: '', payout_percentage: '',
  incentive_basis: '',
  bank_name: '', account_number: '', ifsc_code: '',
  account_holder_name: '', is_active: true,
});

/* ─── Validation per step ─────────────────────────────────── */
const validateStep1 = (form) => {
  const e = {};
  if (!form.name.trim()) e.name = 'Name is required';
  const mob = form.mobile_number.replace(/\D/g, '');
  if (!mob)                   e.mobile_number = 'Mobile is required';
  else if (mob.length !== 10) e.mobile_number = 'Must be 10 digits';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    e.email = 'Invalid email';
  if (form.pan) {
    const pan = form.pan.trim();
    if (pan.length !== 10)
      e.pan = `PAN must be 10 characters (${pan.length}/10)`;
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(pan))
      e.pan = '5 letters · 4 digits · 1 letter (e.g. ABCDE1234F)';
  }
  const pay = Number(form.payout_percentage) || 0;
  if (!form.payout_type || pay === 0) {
    e.payout_percentage = 'Required';
  } else if (pay < 0 || pay > 100) {
    e.payout_percentage = 'Must be 0–100';
  }
  return e;
};

const validateStep2 = (_form) => ({}); // bank fields are optional

/* ─── Style atoms ─────────────────────────────────────────── */
const inp = (err) => ({
  width: '100%', height: 36,
  border: `1px solid ${err ? B.red : B.border}`,
  borderRadius: 8, padding: '0 11px',
  fontSize: 13, color: B.text1, background: B.white,
  outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif",
  boxSizing: 'border-box', transition: 'border 0.13s,box-shadow 0.13s',
  appearance: 'none', WebkitAppearance: 'none',
});
const iFocus = (e) => { e.target.style.borderColor = B.navy; e.target.style.boxShadow = '0 0 0 3px rgba(13,59,94,0.10)'; };
const iBlur  = (e) => { e.target.style.borderColor = B.border; e.target.style.boxShadow = 'none'; };

/* ─── Sub-components ──────────────────────────────────────── */
function Field({ label, required, error, span2, children }) {
  return (
    <div style={{ gridColumn: span2 ? '1/-1' : 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11.5, fontWeight: 600, color: B.text2 }}>
        {label}{required && <span style={{ color: B.red }}> *</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: 11, color: B.red, marginTop: 1 }}>{error}</span>}
    </div>
  );
}

function Sel({ value, onChange, options, placeholder, err }) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={onChange}
        style={{ ...inp(err), cursor: 'pointer', paddingRight: 28 }}
        onFocus={iFocus} onBlur={iBlur}
      >
        <option value="">{placeholder || 'Select…'}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: B.text3, fontSize: 10 }}>▾</span>
    </div>
  );
}

/* ─── Stepper header ──────────────────────────────────────── */
function Stepper({ currentStep, completedSteps }) {
  return (
    <div style={{ padding: '16px 20px 0', background: B.white, flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {STEPS.map((step, idx) => {
          const done    = completedSteps.includes(step.id);
          const active  = currentStep === step.id;
          const isLast  = idx === STEPS.length - 1;
          return (
            <React.Fragment key={step.id}>
              {/* Step node */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: isLast ? 'none' : 1 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12,
                  background: done ? B.green : active ? B.navy : B.navyLight,
                  color:      done ? B.white : active ? B.white : B.text3,
                  border:     active ? `2px solid ${B.navy}` : done ? `2px solid ${B.green}` : `2px solid ${B.border}`,
                  transition: 'all 0.2s',
                  boxShadow:  active ? `0 0 0 4px ${B.navy}22` : 'none',
                }}>
                  {done ? <FiCheck size={14} /> : step.icon}
                </div>
                <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 600, color: active ? B.navy : done ? B.green : B.text3, whiteSpace: 'nowrap' }}>
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div style={{ flex: 1, height: 2, background: done ? B.green : B.border, marginBottom: 22, transition: 'background 0.2s' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: B.border, borderRadius: 2, margin: '14px 0 0', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 2,
          background: `linear-gradient(90deg, ${B.navy}, ${B.navyMid})`,
          width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%`,
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function BsaMaster() {

  /* ── List state ── */
  const [bsaList,    setBsaList]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [apiError,   setApiError]   = useState(null);

  /* ── Drawer + wizard state ── */
  const [drawerOpen,      setDrawerOpen]      = useState(false);
  const [drawerAnim,      setDrawerAnim]      = useState(false);
  const [editingItem,     setEditingItem]     = useState(null);
  const [form,            setForm]            = useState(emptyForm());
  const [errors,          setErrors]          = useState({});
  const [saving,          setSaving]          = useState(false);
  const [currentStep,     setCurrentStep]     = useState(1);
  const [completedSteps,  setCompletedSteps]  = useState([]);
  const [deletingId,      setDeletingId]      = useState(null);

  /* ── Document state ── */
  const [documents,    setDocuments]    = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docDeleting,  setDocDeleting]  = useState(null);
  const [docType,      setDocType]      = useState('KYC_ID');
  const [docFile,      setDocFile]      = useState(null);
  const fileRef = useRef(null);

  /* ── Fetch list ── */
  const fetchList = useCallback(async (pg = 1, isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setApiError(null);
    try {
      const res  = await api.getBsaList({ page: pg });
      const data = res?.data;
      if (Array.isArray(data?.results)) {
        setBsaList(data.results);
        setTotalCount(data.count || 0);
      } else if (Array.isArray(data)) {
        setBsaList(data);
        setTotalCount(data.length);
      } else {
        setBsaList([]);
        setTotalCount(0);
      }
      setPage(pg);
    } catch (err) {
      const status = err?.status || err?.response?.status;
      if      (status === 403) setApiError('Access denied (403).');
      else if (status === 401) setApiError('Session expired. Please log in again.');
      else                     setApiError(err?.message || 'Failed to load BSA list.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchList(1); }, [fetchList]);

  /* ── Drawer helpers ── */
  const openDrawer = () => {
    setDrawerOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setDrawerAnim(true)));
  };
  const closeDrawer = () => {
    setDrawerAnim(false);
    setTimeout(() => {
      setDrawerOpen(false);
      setEditingItem(null);
      setErrors({});
      setDocFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }, 280);
  };

  /* ── Open create ── */
  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm());
    setErrors({});
    setDocuments([]);
    setDocFile(null);
    setDocType('KYC_ID');
    setCurrentStep(1);
    setCompletedSteps([]);
    openDrawer();
  };

  /* ── Open edit ── */
  const openEdit = (item) => {
    setEditingItem(item);
    const disbPay = Number(item.disbursement_payout_percentage) || 0;
    const compPay = Number(item.company_payout_percentage) || 0;
    setForm({
      name:                       item.name                       || '',
      mobile_number:             item.mobile_number             || '',
      email:                  item.email                  || '',
      pan:                    item.pan                    || '',
      gst_number:             item.gst_number             || '',
      constitution:           item.constitution           || '',
      address:              item.address              || '',
      bsa_type:             item.bsa_type             || '',
      payout_type:        disbPay > 0 ? 'disbursement' : (compPay > 0 ? 'company' : 'disbursement'),
      payout_percentage:   disbPay > 0 ? String(item.disbursement_payout_percentage) : (compPay > 0 ? String(item.company_payout_percentage) : ''),
      incentive_basis:       item.incentive_basis       || '',
      bank_name:            item.bank_name            || '',
      account_number:         item.account_number         || '',
      ifsc_code:            item.ifsc_code            || '',
      account_holder_name:  item.account_holder_name  || '',
      is_active:           item.is_active !== false,
    });
    setDocuments(Array.isArray(item.documents) ? item.documents : []);
    setDocFile(null);
    setDocType('KYC_ID');
    setErrors({});
    setCurrentStep(1);
    setCompletedSteps([1, 2]); // mark basic + bank as done for edits
    openDrawer();
  };

  const setF = (field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: undefined }));
  };

  /* ── Step navigation ── */
  const goNext = () => {
    if (currentStep === 1) {
      const errs = validateStep1(form);
      if (Object.keys(errs).length) { setErrors(errs); return; }
      setErrors({});
      setCompletedSteps(p => [...new Set([...p, 1])]);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCompletedSteps(p => [...new Set([...p, 2])]);
      setCurrentStep(3);
    }
  };

  const goBack = () => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
  };

  /* ── Save BSA (called from step 2 footer) ── */
  const handleSave = async () => {
    const errs = validateStep1(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      setCurrentStep(1);
      toast.error('Please fix errors in Basic Info');
      return;
    }
    setSaving(true);
    try {
      const user   = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.id || null;
      const payload = {
        name:                form.name.trim(),
        mobile_number:       form.mobile_number.trim(),
        email:               form.email.trim()                   || null,
        pan:                 form.pan.trim().toUpperCase()       || '',
        gst_number:          form.gst_number.trim()              || '',
        constitution:        form.constitution                   || '',
        address:             form.address.trim()                 || '',
        bsa_type:            form.bsa_type                       || '',
        disbursement_payout_percentage: form.payout_type === 'disbursement' ? (form.payout_percentage || '0.0000') : '0.0000',
        company_payout_percentage:   form.payout_type === 'company'   ? (form.payout_percentage || '0.0000') : '0.0000',
        incentive_basis:     form.incentive_basis                || '',
        bank_name:           form.bank_name.trim()               || '',
        account_number:      form.account_number.trim()          || '',
        ifsc_code:           form.ifsc_code.trim().toUpperCase() || '',
        account_holder_name: form.account_holder_name.trim()    || '',
        is_active:           form.is_active,
        updated_by:          userId,
        ...(editingItem ? {} : { created_by: userId }),
      };

      let savedItem = editingItem;
      if (editingItem) {
        const res = await api.updateBsa(editingItem.id, payload);
        savedItem = res?.data || editingItem;
        toast.success('BSA updated successfully');
      } else {
        const res = await api.createBsa(payload);
        savedItem = res?.data;
        toast.success('BSA created — you can now upload documents');
      }

      setCompletedSteps(p => [...new Set([...p, 1, 2])]);
      setEditingItem(savedItem);   // so step 3 has the id
      setCurrentStep(3);
      fetchList(page);
    } catch (err) {
      const fieldMap = {
        name: 'name', mobile_number: 'mobile_number', email: 'email',
        pan: 'pan', gst_number: 'gst_number', constitution: 'constitution',
        address: 'address', bsa_type: 'bsa_type',
        payout_percentage: 'payout_percentage',
        incentive_basis: 'incentive_basis',
        bank_name: 'bank_name', account_number: 'account_number',
        ifsc_code: 'ifsc_code', account_holder_name: 'account_holder_name',
      };
      const fieldErrors = {};
      let hasFieldErrors = false;
      Object.entries(fieldMap).forEach(([apiKey, formKey]) => {
        if (Array.isArray(err?.[apiKey])) {
          fieldErrors[formKey] = err[apiKey][0];
          hasFieldErrors = true;
        }
      });
      if (hasFieldErrors) {
        setErrors(prev => ({ ...prev, ...fieldErrors }));
        const bankFields = ['bank_name','account_number','ifsc_code','account_holder_name'];
        const hasBasic   = Object.keys(fieldErrors).some(k => !bankFields.includes(k));
        setCurrentStep(hasBasic ? 1 : 2);
        toast.error('Please fix the highlighted errors');
      } else {
        toast.error(err?.message || err?.detail || 'Failed to save BSA');
      }
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete BSA ── */
  const handleDelete = async (item) => {
    if (!window.confirm(`Delete BSA "${item.name}"?\n\nThis cannot be undone.`)) return;
    setDeletingId(item.id);
    try {
      await api.deleteBsa(item.id);
      toast.success('BSA deleted');
      fetchList(bsaList.length === 1 && page > 1 ? page - 1 : page);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete BSA');
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Upload document ── */
  const handleDocUpload = async () => {
    if (!docFile)         { toast.error('Please select a file'); return; }
    if (!editingItem?.id) { toast.error('Save the BSA first (go back to step 2)'); return; }
    setDocUploading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const fd   = new FormData();
      fd.append('document_type', docType);
      fd.append('file',          docFile);
      fd.append('bsa',           editingItem.id);
      if (user?.id) { fd.append('created_by', user.id); fd.append('updated_by', user.id); }
      const res = await api.createBsaDocument(fd);
      setDocuments(prev => [...prev, res.data]);
      setDocFile(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setDocUploading(false);
    }
  };

  /* ── Delete document ── */
  const handleDocDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    setDocDeleting(docId);
    try {
      await api.deleteBsaDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete document');
    } finally {
      setDocDeleting(null);
    }
  };

  /* ── Client-side search ── */
  const filtered = bsaList.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.name?.toLowerCase().includes(q)  ||
      b.code?.toLowerCase().includes(q)  ||
      b.mobile_number?.includes(q)       ||
      b.email?.toLowerCase().includes(q) ||
      b.pan?.toLowerCase().includes(q)   ||
      bsaTypeLabel(b.bsa_type).toLowerCase().includes(q)
    );
  });

  const activeCount   = bsaList.filter(b => b.is_active !== false).length;
  const inactiveCount = bsaList.filter(b => b.is_active === false).length;

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <Layout pageTitle="BSA Master">
      <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif" }}>

        {/* ── PAGE HEADER ── */}
        <div style={{
          background: `linear-gradient(128deg,${B.navy} 0%,${B.navyMid} 60%,#1B6091 100%)`,
          borderRadius: 14, padding: '10px 20px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 10,
          boxShadow: '0 4px 16px rgba(13,59,94,0.18)',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: 2 }}>Masters</div>
            <h2 style={{ margin: 0, color: B.white, fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
              BSA Master
            </h2>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 3 }}>
              Manage Business Sourcing Associates
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => fetchList(page, true)}
              disabled={refreshing}
              title="Refresh"
              style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', color: B.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiRefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            </button>
            <button
              onClick={openCreate}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: B.gold, color: B.navy, border: 'none', borderRadius: 9, padding: '8px 16px', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 2px 8px rgba(245,166,35,0.35)' }}>
              <FiPlus size={15} /> Add BSA
            </button>
          </div>
        </div>

        {/* ── STAT CARDS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total BSAs', value: totalCount,    color: B.navy  },
            { label: 'Active',     value: activeCount,   color: B.green },
            { label: 'Inactive',   value: inactiveCount, color: B.red   },
          ].map(card => (
            <div key={card.label} style={{ background: B.white, borderRadius: 12, border: `1px solid ${B.border}`, padding: '14px 16px 12px', boxShadow: '0 2px 6px rgba(13,59,94,0.07)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.text3, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{card.label}</div>
              {loading
                ? <div className="shimmer" style={{ height: 38, width: '50%', borderRadius: 6 }} />
                : <div style={{ fontSize: 34, fontWeight: 800, color: card.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{card.value}</div>
              }
            </div>
          ))}
        </div>

        {/* ── TABLE CARD ── */}
        <div style={{ background: B.white, borderRadius: 12, border: `1px solid ${B.border}`, boxShadow: '0 2px 6px rgba(13,59,94,0.06)', overflow: 'hidden' }}>

          {/* Toolbar */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiBriefcase size={14} style={{ color: B.navy, opacity: 0.75 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: B.text1 }}>All BSAs</span>
              {!loading && (
                <span style={{ background: B.navyLight, color: B.navy, padding: '2px 8px', borderRadius: 20, fontSize: 10.5, fontWeight: 700 }}>
                  {totalCount}
                </span>
              )}
            </div>
            <div style={{ position: 'relative', width: 280 }}>
              <FiSearch size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: B.text3, pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search name, code, mobile, PAN…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', height: 34, border: `1px solid ${B.border}`, borderRadius: 8, padding: '0 12px 0 32px', fontSize: 12.5, color: B.text1, background: B.bg, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = B.navy}
                onBlur={e  => e.target.style.borderColor = B.border}
              />
            </div>
          </div>

          {loading && (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1,2,3,4,5].map(i => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8 }} />)}
            </div>
          )}

          {!loading && apiError && (
            <div style={{ padding: '36px 24px', textAlign: 'center' }}>
              <div style={{ background: B.redLight, border: `1px solid ${B.redBorder}`, borderRadius: 12, padding: '20px 24px', display: 'inline-block', maxWidth: 500, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <FiAlertCircle size={16} style={{ color: B.red }} />
                  <span style={{ fontWeight: 700, color: B.red, fontSize: 13 }}>Cannot Load BSA List</span>
                </div>
                <p style={{ color: '#7F1D1D', fontSize: 12.5, margin: 0 }}>{apiError}</p>
                <button onClick={() => fetchList(1)} style={{ marginTop: 12, background: B.red, color: B.white, border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
              </div>
            </div>
          )}

          {!loading && !apiError && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '52px 24px' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: B.navyLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <FiBriefcase size={22} style={{ color: B.navy, opacity: 0.45 }} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: B.text1, marginBottom: 6 }}>
                {search ? 'No BSAs match your search' : 'No BSAs yet'}
              </div>
              <div style={{ fontSize: 12.5, color: B.text3 }}>
                {search ? 'Try a different keyword' : 'Click "Add BSA" to create the first entry'}
              </div>
            </div>
          )}

          {!loading && !apiError && filtered.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                <thead>
                  <tr style={{ background: '#F7F9FB' }}>
                    {['#','BSA','Mobile','Type','Constitution','Payout %','Incentive Basis','Status','Actions'].map((col, i) => (
                      <th key={col} style={{ padding: '10px 14px', fontSize: 10.5, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: i === 9 ? 'right' : 'left', borderBottom: `1px solid ${B.border}`, whiteSpace: 'nowrap' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, idx) => {
                    const isDeleting = deletingId === item.id;
                    const isLast     = idx === filtered.length - 1;
                    const tcolor     = TYPE_COLORS[item.bsa_type] || TYPE_COLORS.OTHER;
                    const acol       = avatarColor(item.name);
                    const initials   = (item.name || '?').trim().split(' ').map(p => p[0]).slice(0,2).join('').toUpperCase();
                    return (
                      <tr key={item.id} className="bsa-row" style={{ background: B.white, opacity: isDeleting ? 0.5 : 1, transition: 'all 0.12s' }}>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}`, fontSize: 12, color: B.text3, fontWeight: 600 }}>
                          {(page-1)*PAGE_SIZE+idx+1}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 34, height: 34, borderRadius: '50%', background: acol+'20', border: `1.5px solid ${acol}40`, color: acol, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                              {initials}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13, color: B.text1 }}>{item.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                {item.code && <span style={{ fontSize: 10.5, fontFamily: 'monospace', background: B.greenLight, color: B.green, border: `1px solid ${B.greenBorder}`, padding: '1px 6px', borderRadius: 5 }}>{item.code}</span>}
                                {item.email && <span style={{ fontSize: 11, color: B.text3 }}>{item.email}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}`, fontSize: 12.5, color: B.text2 }}>
                          {item.mobile_number || <span style={{ color: B.text3 }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}` }}>
                          {item.bsa_type
                            ? <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: tcolor.bg, color: tcolor.color, border: `1px solid ${tcolor.border}`, whiteSpace: 'nowrap' }}>{bsaTypeLabel(item.bsa_type)}</span>
                            : <span style={{ color: B.text3, fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}`, fontSize: 12.5, color: B.text2 }}>
                          {constLabel(item.constitution) || <span style={{ color: B.text3 }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}` }}>
                          {(() => {
                            const disb = Number(item.disbursement_payout_percentage) || 0;
                            const comp = Number(item.company_payout_percentage) || 0;
                            if (disb > 0) return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: B.goldLight, color: '#854D0E', border: '1px solid #FDE68A' }}>Disb. {item.disbursement_payout_percentage}%</span>;
                            if (comp > 0) return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: B.navyLight, color: B.navy, border: '1px solid #BFDBFE' }}>Co. {item.company_payout_percentage}%</span>;
                            return <span style={{ color: B.text3, fontSize: 12 }}>—</span>;
                          })()}
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}`, fontSize: 12, color: B.text2 }}>
                          {item.incentive_basis
                            ? <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: B.navyLight, color: B.navy, border: `1px solid #BFDBFE` }}>{incentiveBasisLabel(item.incentive_basis)}</span>
                            : <span style={{ color: B.text3, fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}` }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: item.is_active !== false ? B.greenLight : B.redLight, color: item.is_active !== false ? B.green : B.red, border: `1px solid ${item.is_active !== false ? B.greenBorder : B.redBorder}` }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: item.is_active !== false ? B.green : B.red }} />
                            {item.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', borderBottom: isLast ? 'none' : `1px solid ${B.border}`, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="bsa-act-btn" onClick={() => openEdit(item)} title="Edit" disabled={isDeleting} style={{ color: B.navy }}><FiEdit2 size={13} /></button>
                            <button className="bsa-act-btn" onClick={() => handleDelete(item)} title="Delete" disabled={isDeleting} style={{ color: B.red }}>
                              {isDeleting ? <div style={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${B.red}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} /> : <FiTrash2 size={13} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalCount > PAGE_SIZE && (
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${B.border}` }}>
              <Pagination currentPage={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={pg => fetchList(pg)} />
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div style={{ padding: '9px 16px', borderTop: `1px solid ${B.border}`, fontSize: 11.5, color: B.text3, background: '#FAFBFC' }}>
              Showing {filtered.length} of {bsaList.length} BSA{bsaList.length !== 1 ? 's' : ''}
              {search && ` matching "${search}"`}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          BACKDROP
      ═══════════════════════════════════════════════════ */}
      {drawerOpen && (
        <div
          onClick={closeDrawer}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.40)', zIndex: 1000, opacity: drawerAnim ? 1 : 0, transition: 'opacity 0.28s ease' }}
        />
      )}

      {/* ═══════════════════════════════════════════════════
          RIGHT DRAWER
      ═══════════════════════════════════════════════════ */}
      {drawerOpen && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: DRAWER_WIDTH, maxWidth: '100vw',
          background: B.white, zIndex: 1001,
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 40px rgba(10,22,40,0.18)',
          transform: drawerAnim ? 'translateX(0)' : `translateX(${DRAWER_WIDTH}px)`,
          transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
        }}>

          {/* Drawer header */}
          <div style={{ background: `linear-gradient(128deg,${B.navy} 0%,${B.navyMid} 60%,#1B6091 100%)`, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: 2 }}>BSA Master</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: B.white, letterSpacing: '-0.02em' }}>
                {editingItem ? `Edit BSA` : 'Add New BSA'}
              </div>
              {editingItem?.name && (
                <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                  {editingItem.name}{editingItem.code ? ` · ${editingItem.code}` : ''}
                </div>
              )}
            </div>
            <button onClick={closeDrawer} style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', color: B.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiX size={16} />
            </button>
          </div>

          {/* Stepper */}
          <Stepper currentStep={currentStep} completedSteps={completedSteps} />

          {/* Step label */}
          <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: B.text1 }}>
              Step {currentStep} of {STEPS.length} — {STEPS[currentStep-1].label}
            </div>
            <div style={{ fontSize: 11.5, color: B.text3, marginTop: 2 }}>
              {currentStep === 1 && 'Fill in the basic details and contact info'}
              {currentStep === 2 && 'Enter bank account details for payout processing'}
              {currentStep === 3 && 'Upload supporting documents (optional)'}
            </div>
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

            {/* ── STEP 1: Basic Info ── */}
            {currentStep === 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>

                <Field label="Full Name" required error={errors.name} span2>
                  <input value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. Ramesh Patel" style={inp(errors.name)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="Mobile Number" required error={errors.mobile_number}>
                  <input value={form.mobile_number} onChange={e => setF('mobile_number', e.target.value)} placeholder="10-digit mobile" maxLength={10} style={inp(errors.mobile_number)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="Email Address" error={errors.email}>
                  <input value={form.email} onChange={e => setF('email', e.target.value)} placeholder="email@example.com" type="email" style={inp(errors.email)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="PAN Number" error={errors.pan}>
                  <input value={form.pan} onChange={e => setF('pan', e.target.value.toUpperCase())} placeholder="AAAAA9999A" maxLength={10} style={inp(errors.pan)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="GST Number" error={errors.gst_number}>
                  <input value={form.gst_number} onChange={e => setF('gst_number', e.target.value.toUpperCase())} placeholder="15-char GSTIN" maxLength={15} style={inp(errors.gst_number)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="BSA Type" error={errors.bsa_type}>
                  <Sel value={form.bsa_type} onChange={e => setF('bsa_type', e.target.value)} options={BSA_TYPES} placeholder="Select type" err={errors.bsa_type} />
                </Field>

                <Field label="Constitution" error={errors.constitution}>
                  <Sel value={form.constitution} onChange={e => setF('constitution', e.target.value)} options={CONSTITUTIONS} placeholder="Select constitution" err={errors.constitution} />
                </Field>

                <Field label="Payout Type" error={errors.payout_type}>
                  <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: B.text1 }}>
                      <input type="radio" name="payout_type" checked={form.payout_type === 'disbursement'} onChange={() => { setF('payout_type', 'disbursement'); setF('payout_percentage', ''); }} style={{ accentColor: B.navy, width: 14, height: 14 }} />
                      Disbursement
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: B.text1 }}>
                      <input type="radio" name="payout_type" checked={form.payout_type === 'company'} onChange={() => { setF('payout_type', 'company'); setF('payout_percentage', ''); }} style={{ accentColor: B.navy, width: 14, height: 14 }} />
                      Company
                    </label>
                  </div>
                </Field>

                <Field label="Payout %" error={errors.payout_percentage}>
                  <input value={form.payout_percentage} onChange={e => setF('payout_percentage', e.target.value)} placeholder="e.g. 1.50" type="number" min="0" max="100" step="0.01" style={inp(errors.payout_percentage)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="Basis of Incentive Calculation" error={errors.incentive_basis}>
                  <Sel value={form.incentive_basis} onChange={e => setF('incentive_basis', e.target.value)} options={INCENTIVE_BASIS} placeholder="Select basis" err={errors.incentive_basis} />
                </Field>

                <Field label="Address" error={errors.address} span2>
                  <textarea value={form.address} onChange={e => setF('address', e.target.value)} rows={2} placeholder="Full address" style={{ ...inp(errors.address), height: 'auto', padding: '8px 11px', resize: 'vertical', lineHeight: 1.5 }} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="Status" span2>
                  <div style={{ display: 'flex', gap: 20 }}>
                    {[{ v: true, l: 'Active' }, { v: false, l: 'Inactive' }].map(opt => (
                      <label key={String(opt.v)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: B.text1 }}>
                        <input type="radio" name="bsa_is_active" checked={form.is_active === opt.v} onChange={() => setF('is_active', opt.v)} style={{ accentColor: B.navy, width: 14, height: 14 }} />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </Field>

              </div>
            )}

            {/* ── STEP 2: Bank Details ── */}
            {currentStep === 2 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>

                <Field label="Bank Name" error={errors.bank_name} span2>
                  <input value={form.bank_name} onChange={e => setF('bank_name', e.target.value)} placeholder="e.g. HDFC Bank" style={inp(errors.bank_name)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="Account Holder Name" error={errors.account_holder_name} span2>
                  <input value={form.account_holder_name} onChange={e => setF('account_holder_name', e.target.value)} placeholder="Name as per bank records" style={inp(errors.account_holder_name)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="Account Number" error={errors.account_number}>
                  <input value={form.account_number} onChange={e => setF('account_number', e.target.value)} placeholder="Bank account number" style={inp(errors.account_number)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <Field label="IFSC Code" error={errors.ifsc_code}>
                  <input value={form.ifsc_code} onChange={e => setF('ifsc_code', e.target.value.toUpperCase())} placeholder="e.g. HDFC0001234" maxLength={11} style={inp(errors.ifsc_code)} onFocus={iFocus} onBlur={iBlur} />
                </Field>

                <div style={{ gridColumn: '1/-1', background: B.navyLight, borderRadius: 8, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <FiCreditCard size={13} style={{ color: B.navy, marginTop: 1, flexShrink: 0 }} />
                  <span style={{ fontSize: 11.5, color: B.navy, lineHeight: 1.6 }}>
                    Bank fields are optional but required for payout processing. You can fill them later by editing this BSA.
                  </span>
                </div>

              </div>
            )}

            {/* ── STEP 3: Documents ── */}
            {currentStep === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* New unsaved BSA warning */}
                {!editingItem && (
                  <div style={{ background: B.amberLight, border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <FiAlertCircle size={13} style={{ color: B.amber, marginTop: 1, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#92400E', lineHeight: 1.6 }}>
                      Go back to Step 2 and click <strong>Save BSA</strong> first. Then you can upload documents.
                    </span>
                  </div>
                )}

                {/* Success indicator */}
                {editingItem && (
                  <div style={{ background: B.greenLight, border: `1px solid ${B.greenBorder}`, borderRadius: 8, padding: '10px 13px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiCheck size={13} style={{ color: B.green, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
                      BSA saved — you can now upload documents below.
                    </span>
                  </div>
                )}

                {/* Upload panel */}
                {editingItem && (
                  <div style={{ background: '#F8FAFC', border: `1px solid ${B.border}`, borderRadius: 10, padding: '14px' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: B.text1, marginBottom: 12 }}>Upload New Document</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <Field label="Document Type">
                        <Sel value={docType} onChange={e => setDocType(e.target.value)} options={DOC_TYPES} />
                      </Field>
                      <Field label="Select File">
                        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={e => setDocFile(e.target.files[0] || null)}
                          style={{ width: '100%', height: 36, border: `1px solid ${B.border}`, borderRadius: 8, padding: '0 10px', fontSize: 12.5, color: B.text1, background: B.white, boxSizing: 'border-box', cursor: 'pointer', fontFamily: 'inherit' }} />
                        {docFile && <span style={{ fontSize: 11, color: B.green, marginTop: 3, display: 'block' }}>✓ {docFile.name}</span>}
                      </Field>
                      <button
                        onClick={handleDocUpload}
                        disabled={docUploading || !docFile}
                        style={{ height: 36, padding: '0 16px', background: (docUploading || !docFile) ? B.border : B.navy, color: (docUploading || !docFile) ? B.text3 : B.white, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: (docUploading || !docFile) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        {docUploading
                          ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', animation: 'spin 0.8s linear infinite' }} /> Uploading…</>
                          : <><FiUpload size={13} /> Upload Document</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Document list */}
                {documents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: B.navyLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                      <FiFileText size={18} style={{ color: B.navy, opacity: 0.4 }} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: B.text2 }}>No documents yet</div>
                    <div style={{ fontSize: 12, color: B.text3, marginTop: 3 }}>
                      {editingItem ? 'Upload documents using the form above.' : 'Save the BSA first, then upload.'}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: B.text2 }}>Uploaded Documents ({documents.length})</div>
                    {documents.map(doc => {
                      const isDel = docDeleting === doc.id;
                      return (
                        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#FAFBFC', border: `1px solid ${B.border}`, borderRadius: 9 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 7, background: B.navyLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FiFile size={13} style={{ color: B.navy }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: B.text1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileName(doc.file)}</div>
                            <div style={{ fontSize: 11, color: B.text3, marginTop: 2 }}>
                              {docTypeLabel(doc.document_type)}
                              {doc.created_at && ` · ${new Date(doc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                            </div>
                          </div>
                          {doc.file && (
                            <a href={doc.file} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 11.5, color: B.sky, fontWeight: 600, textDecoration: 'none', padding: '3px 9px', background: B.skyLight, borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              View
                            </a>
                          )}
                          <button onClick={() => handleDocDelete(doc.id)} disabled={isDel}
                            style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${B.border}`, background: B.white, color: B.red, cursor: isDel ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: isDel ? 0.5 : 1 }}>
                            {isDel ? <div style={{ width: 10, height: 10, borderRadius: '50%', border: `2px solid ${B.red}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} /> : <FiTrash size={11} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            )}

          </div>

          {/* ── Footer ── */}
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${B.border}`, background: '#FAFBFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>

            {/* Left: Back or Cancel */}
            {currentStep === 1
              ? <button onClick={closeDrawer} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${B.border}`, background: B.white, color: B.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              : <button onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 16px', borderRadius: 8, border: `1px solid ${B.border}`, background: B.white, color: B.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <FiChevronLeft size={14} /> Back
                </button>
            }

            {/* Right: Next or Save or Finish */}
            <div style={{ display: 'flex', gap: 8 }}>
              {currentStep === 1 && (
                <button onClick={goNext} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 20px', borderRadius: 8, border: 'none', background: B.navy, color: B.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(13,59,94,0.25)' }}>
                  Next <FiChevronRight size={14} />
                </button>
              )}
              {currentStep === 2 && (
                <button onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 8, border: 'none', background: saving ? B.navyMid : B.navy, color: B.white, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(13,59,94,0.25)' }}>
                  {saving
                    ? <><div style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', animation: 'spin 0.8s linear infinite' }} /> Saving…</>
                    : <><FiSave size={13} /> {editingItem ? 'Update BSA' : 'Save BSA'}</>}
                </button>
              )}
              {currentStep === 3 && (
                <button onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 8, border: 'none', background: B.green, color: B.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(22,163,74,0.25)' }}>
                  <FiCheck size={13} /> Done
                </button>
              )}
            </div>

          </div>

        </div>
      )}

      <style>{`
        @keyframes spin    { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes shimmer { 0% { background-position: -400px 0 } 100% { background-position: 400px 0 } }
        .shimmer { background: linear-gradient(90deg,${B.border} 25%,#E8ECF0 50%,${B.border} 75%); background-size: 400px 100%; animation: shimmer 1.4s infinite; border-radius: 6px; }
        .bsa-row:hover { background: #F7F9FB !important; }
        .bsa-act-btn { width:30px; height:30px; border-radius:8px; border:1px solid ${B.border}; background:${B.white}; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.13s; }
        .bsa-act-btn:hover { border-color:currentColor; background:${B.navyLight}; }
        .bsa-act-btn:disabled { opacity:0.4; cursor:not-allowed; }
      `}</style>
    </Layout>
  );
}
